import React, { useState, useRef, useCallback } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination } from 'swiper/modules';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../translations/translations';
import useScrollAnimation from '../hooks/useScrollAnimation';
import '../styles/scrollAnimations.css';

import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

import './TestimonialSlider.css';

const VideoPlayer = ({ src, poster, watchLabel }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const videoRef = useRef(null);

  const handlePlay = useCallback(() => {
    setIsPlaying(true);
    requestAnimationFrame(() => {
      if (videoRef.current) {
        videoRef.current.load();
        videoRef.current.play().catch(() => {});
      }
    });
  }, []);

  const handleVideoClick = useCallback(() => {
    if (videoRef.current) {
      if (videoRef.current.paused) {
        videoRef.current.play();
      } else {
        videoRef.current.pause();
        setIsPlaying(false);
      }
    }
  }, []);

  if (!isPlaying) {
    return (
      <div className="testimonial-video-container" onClick={handlePlay}>
        <img src={poster} alt="Video thumbnail" className="testimonial-image" />
        <div className="testimonial-play-overlay">
          <button className="testimonial-play-btn" aria-label="Play video">
            <svg width="28" height="32" viewBox="0 0 28 32" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M26 14.268C27.3333 15.0378 27.3333 16.9623 26 17.7321L4 30.4282C2.66667 31.198 1 30.2358 1 28.6962L1 3.30385C1 1.76425 2.66667 0.802047 4 1.57185L26 14.268Z" fill="white"/>
            </svg>
          </button>
          <span className="testimonial-play-label">{watchLabel}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="testimonial-video-container" onClick={handleVideoClick}>
      <video
        ref={videoRef}
        className="testimonial-video"
        playsInline
        poster={poster}
      >
        <source src={src} type="video/mp4" />
      </video>
    </div>
  );
};

const TestimonialSlider = () => {
  const { language } = useLanguage();
  const t = translations[language].testimonialSlider;
  const testimonials = t.testimonials;
  const [containerRef, containerVisible] = useScrollAnimation({ threshold: 0.1 });

  return (
    <section className="testimonial-slider-section">
      <div 
        ref={containerRef}
        className={`testimonial-slider-wrapper scroll-fade-in ${containerVisible ? 'is-visible' : ''}`}
      >
        <Swiper
          modules={[Navigation, Pagination]}
          spaceBetween={0}
          slidesPerView={1}
          centeredSlides={true}
          loop={false}
          speed={600}
          autoplay={false}
          allowTouchMove={false}
          simulateTouch={false}
          grabCursor={false}
          pagination={{
            clickable: true,
            el: '.testimonial-pagination',
          }}
          navigation={{
            nextEl: '.testimonial-button-next',
            prevEl: '.testimonial-button-prev',
          }}
          breakpoints={{
            1200: {
              slidesPerView: 1.4,
            },
            1600: {
              slidesPerView: 1.6,
            }
          }}
          className="testimonial-swiper"
        >
          {testimonials.map((testimonial, index) => (
            <SwiperSlide key={index}>
              <div className="testimonial-slide">
                <div className="testimonial-image-wrapper">
                  {testimonial.video ? (
                    <VideoPlayer src={testimonial.video} poster={testimonial.poster} watchLabel={t.watchVideo} />
                  ) : (
                    <img 
                      src={testimonial.image} 
                      alt={testimonial.name}
                      className="testimonial-image"
                    />
                  )}
                </div>

                {/* Content */}
                <div className="testimonial-content">
                  {/* Quote Icon */}
                  <div className="testimonial-quote-icon">
                    <svg width="56" height="56" viewBox="0 0 56 56" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M16.333 28H9.333C9.333 20.268 15.601 14 23.333 14V18.667C17.443 18.667 12.667 23.443 12.667 29.333H16.333C19.096 29.333 21.333 31.57 21.333 34.333V40.333C21.333 43.096 19.096 45.333 16.333 45.333H11.667C8.904 45.333 6.667 43.096 6.667 40.333V34.333C6.667 31.57 8.904 29.333 11.667 29.333H16.333V28Z" fill="#ffffff"/>
                      <path d="M39.667 28H32.667C32.667 20.268 38.935 14 46.667 14V18.667C40.777 18.667 36 23.443 36 29.333H39.667C42.43 29.333 44.667 31.57 44.667 34.333V40.333C44.667 43.096 42.43 45.333 39.667 45.333H35C32.237 45.333 30 43.096 30 40.333V34.333C30 31.57 32.237 29.333 35 29.333H39.667V28Z" fill="#ffffff"/>
                    </svg>
                  </div>

                  {/* Quote Text */}
                  <p className="testimonial-quote">
                    {testimonial.quote}{' '}
                    <span className="testimonial-highlight">{testimonial.highlight}</span>
                  </p>

                  {/* Author */}
                  <div className="testimonial-author">
                    <h4 className="testimonial-name">{testimonial.name}</h4>
                    <p className="testimonial-title">{testimonial.title}</p>
                  </div>
                </div>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>

        {/* Navigation Buttons - Symmetrically positioned */}
        <button className="testimonial-button-prev testimonial-nav-btn">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        <button className="testimonial-button-next testimonial-nav-btn">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>

        {/* Custom Pagination */}
        <div className="testimonial-pagination"></div>
      </div>
    </section>
  );
};

export default TestimonialSlider;
